import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useMutation } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth/store";
import { CreditCard, ExternalLink, Unlink } from "lucide-react";
import { startStripeConnect } from "@/lib/api";
import { toast } from "sonner";

export default function StripeConnectSettings() {
  const { user } = useAuthStore();

  const startConnectMutation = useMutation({
    mutationFn: async () => startStripeConnect(),
    onSuccess: (data) => {
      if (data?.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        toast.error("Stripe did not return a redirect URL");
      }
    },
    onError: (err: any) => {
      toast.error(err?.message || "Failed to start Stripe Connect");
    },
  });
  
  return (
    <div className="max-w-2xl mx-auto mt-8">
      <Card className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">Stripe Connect Settings</h1>
          <p className="text-muted-foreground">
            Connect your Stripe account to start accepting payments for your products
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold mb-2">What is Stripe Connect?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Stripe Connect allows you to accept payments directly to your bank account for products you sell through this platform.
          </p>
          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
            <li>Secure payment processing</li>
            <li>Accept credit cards, debit cards, and more</li>
            <li>Fast payouts to your bank account</li>
            <li>24/7 customer support from Stripe</li>
          </ul>
        </div>

        <div className="space-y-4">
          <Alert>
            <AlertTitle>Global connection</AlertTitle>
            <AlertDescription>
              Stripe Connect is configured once per account (not per site).
            </AlertDescription>
          </Alert>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              variant="default"
              className="w-full sm:w-auto"
              disabled={startConnectMutation.isPending}
              onClick={() => startConnectMutation.mutate()}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              {startConnectMutation.isPending ? "Opening…" : "Connect Stripe"}
            </Button>

            <form action="/stripe_connect" method="post" className="w-full sm:w-auto">
              {/* rails-ujs/turbo reads csrf from meta */}
              <input type="hidden" name="_method" value="delete" />
              <Button type="submit" variant="outline" className="w-full sm:w-auto">
                <Unlink className="mr-2 h-4 w-4" />
                Disconnect
              </Button>
            </form>
          </div>

          <p className="text-sm text-muted-foreground">
            After connecting, you can sell products and receive payouts to your bank account via Stripe.
          </p>
        </div>

        <div className="mt-6 pt-6 border-t">
          <h3 className="font-semibold mb-2">Need Help?</h3>
          <p className="text-sm text-muted-foreground">
            Visit the <a href="https://stripe.com/docs/connect" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Stripe Connect documentation</a> for more information.
          </p>
        </div>
      </Card>
    </div>
  );
}
